import { useBusinessContext } from '@context'
import { useCallback, useReducer, useState } from 'react'
import { fetchDocuments, fetchFiles, IBusiness, IFile } from '@query'
import axios from 'axios'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'

const getZipFileName = (business: Partial<IBusiness>): string => `${business.slug} ${business.name}`
const DEFAULT_PAGE_SIZE = 50
const UNCATEGORIZED_FOLDER_NAME = 'UNCATEGORIZED'

const getFileNameFromUrl = (url: string): string => {
  try {
    const parsedUrl = new URL(url)
    const filePath = parsedUrl.pathname
    const name = filePath.split('/').pop()
    return name || 'attachment'
  } catch {
    const [head] = url.split('?')
    const name = head.split('/').pop()
    return name || 'attachment'
  }
}

const sanitizePathSegment = (name: string): string => {
  const sanitized = name
    // eslint-disable-next-line no-control-regex
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

  return sanitized || 'attachment'
}

const getAttachmentBaseName = (file: IFile): string => {
  const fileName = file.name?.trim()
  if (fileName) return fileName
  if (!file.file) return 'attachment'
  return getFileNameFromUrl(file.file)
}

const getLinkedAttachmentPath = (documentNumber: string, baseName: string): string => {
  const sanitizedDocumentNumber = sanitizePathSegment(documentNumber)
  const sanitizedBaseName = sanitizePathSegment(baseName)
  return `${sanitizedDocumentNumber}/${sanitizePathSegment(
    `${sanitizedDocumentNumber} ${sanitizedBaseName}`
  )}`
}

const getUncategorizedAttachmentPath = (baseName: string): string =>
  `${UNCATEGORIZED_FOLDER_NAME}/${sanitizePathSegment(baseName)}`

const addCopyIndexToPath = (path: string, copyIndex: number): string => {
  const lastSlashIndex = path.lastIndexOf('/')
  const directoryPrefix = lastSlashIndex >= 0 ? path.slice(0, lastSlashIndex + 1) : ''
  const fileName = lastSlashIndex >= 0 ? path.slice(lastSlashIndex + 1) : path
  const extensionIndex = fileName.lastIndexOf('.')

  if (extensionIndex > 0) {
    const baseName = fileName.slice(0, extensionIndex)
    const extension = fileName.slice(extensionIndex)
    return `${directoryPrefix}${baseName} (${copyIndex})${extension}`
  }

  return `${directoryPrefix}${fileName} (${copyIndex})`
}

const getUniqueZipPath = (path: string, pathCounter: Map<string, number>): string => {
  const currentCount = pathCounter.get(path) ?? 0
  pathCounter.set(path, currentCount + 1)

  if (currentCount === 0) {
    return path
  }

  return addCopyIndexToPath(path, currentCount)
}

const buildAttachmentDocumentMap = async (slug: string): Promise<Map<number, string[]>> => {
  const attachmentDocumentMap = new Map<number, string[]>()
  let currentPage = 1
  let hasNextPage = true

  while (hasNextPage) {
    const data = await fetchDocuments({ slug }, { page: currentPage, page_size: DEFAULT_PAGE_SIZE })
    for (const document of data.results) {
      const documentNumber = document.number?.trim()
      if (!documentNumber) continue

      for (const attachmentId of document.attachment_ids ?? []) {
        const normalizedAttachmentId = Number(attachmentId)
        if (Number.isNaN(normalizedAttachmentId)) continue

        const existingDocumentNumbers = attachmentDocumentMap.get(normalizedAttachmentId) ?? []
        if (!existingDocumentNumbers.includes(documentNumber)) {
          existingDocumentNumbers.push(documentNumber)
          attachmentDocumentMap.set(normalizedAttachmentId, existingDocumentNumbers)
        }
      }
    }

    if (data.next) {
      currentPage = data.next
    } else {
      hasNextPage = false
    }
  }

  return attachmentDocumentMap
}

const getZipPathsForAttachment = (
  file: IFile,
  attachmentDocumentMap: Map<number, string[]>
): string[] => {
  const baseName = getAttachmentBaseName(file)
  const documentNumbers = attachmentDocumentMap.get(Number(file.id)) ?? []
  if (!documentNumbers.length) {
    return [getUncategorizedAttachmentPath(baseName)]
  }

  return documentNumbers.map(documentNumber => getLinkedAttachmentPath(documentNumber, baseName))
}

const createDownloadedBlob = async (file: IFile): Promise<Blob | null> => {
  if (!file.file) return null
  const response = await axios.get(file.file, {
    decompress: false,
    responseType: 'arraybuffer'
  })
  return new Blob([response.data], { type: file.type })
}

interface DownloadState {
  isLoading: boolean
  isError: boolean
  isSuccess: boolean
  totalFiles: number
  filesDownloaded: number
}

type Action =
  | { type: 'START' }
  | { type: 'SET_TOTAL_FILES'; payload: number }
  | { type: 'INCREMENT_FILES_DOWNLOADED' }
  | { type: 'SUCCESS' }
  | { type: 'ERROR' }
  | { type: 'RESET' }

const initialState: DownloadState = {
  isLoading: false,
  isError: false,
  isSuccess: false,
  totalFiles: Infinity,
  filesDownloaded: 0
}

function reducer(state: DownloadState, action: Action): DownloadState {
  switch (action.type) {
    case 'START':
      return { ...initialState, isLoading: true }
    case 'SET_TOTAL_FILES':
      return { ...state, totalFiles: action.payload }
    case 'INCREMENT_FILES_DOWNLOADED':
      return { ...state, filesDownloaded: state.filesDownloaded + 1 }
    case 'SUCCESS':
      return { ...state, isLoading: false, isSuccess: true }
    case 'ERROR':
      return { ...state, isLoading: false, isError: true }
    case 'RESET':
      return initialState
    default:
      return state
  }
}

interface PropsIn {
  onSuccess?: () => void
  onError?: () => void
}

interface PropsOut {
  state: DownloadState
  progress: number
  startZipGeneration: () => Promise<void>
  downloadZip: () => void
  reset: () => void
}

export const useDownloadFilesAsZip = ({ onSuccess, onError }: PropsIn): PropsOut => {
  const { slug, data: business } = useBusinessContext()
  const [state, dispatch] = useReducer(reducer, initialState)
  const [blob, setBlob] = useState<Blob>(null)
  const progress = Math.ceil((state.filesDownloaded / state.totalFiles) * 100)

  const _onSuccess = () => {
    dispatch({ type: 'SUCCESS' })
    onSuccess?.()
  }

  const _onError = () => {
    dispatch({ type: 'ERROR' })
    onError?.()
  }

  const _saveAs = (blobResult: Blob) => {
    saveAs(blobResult, getZipFileName(business))
  }

  const generateZip = async (zip: JSZip) => {
    const blobResult = await zip.generateAsync({ type: 'blob' })
    setBlob(blobResult)
    _onSuccess()
    _saveAs(blobResult)
  }

  const downloadZip = () => {
    if (blob) _saveAs(blob)
  }

  const fetchPages = useCallback(
    async (startPage: number = 1): Promise<JSZip> => {
      const zip = new JSZip()
      const pathCounter = new Map<string, number>()
      const attachmentDocumentMap = await buildAttachmentDocumentMap(slug)
      let currentPage = startPage
      let hasNextPage = true

      while (hasNextPage) {
        const data = await fetchFiles({ slug }, { page: currentPage, page_size: DEFAULT_PAGE_SIZE })
        dispatch({ type: 'SET_TOTAL_FILES', payload: data.count })

        // If any file fails, Promise.all rejects and the process stops.
        const filePromises = data.results.map(async file => {
          const downloadedBlob = await createDownloadedBlob(file)
          dispatch({ type: 'INCREMENT_FILES_DOWNLOADED' })
          if (!downloadedBlob) return null
          const zipPaths = getZipPathsForAttachment(file, attachmentDocumentMap)
          return { downloadedBlob, zipPaths }
        })

        const downloadedFiles = await Promise.all(filePromises)
        downloadedFiles.forEach(downloaded => {
          if (!downloaded) return
          const { downloadedBlob, zipPaths } = downloaded
          zipPaths.forEach(zipPath => {
            const uniquePath = getUniqueZipPath(zipPath, pathCounter)
            zip.file(uniquePath, downloadedBlob)
          })
        })

        if (data.next) {
          currentPage = data.next
        } else {
          hasNextPage = false
        }
      }
      return zip
    },
    [slug]
  )

  const startZipGeneration = useCallback(async () => {
    try {
      setBlob(null)
      dispatch({ type: 'START' })
      const zip = await fetchPages()
      await generateZip(zip)
    } catch (error) {
      console.error('Error during zip download:', error)
      _onError()
    }
  }, [fetchPages])

  const reset = useCallback(() => {
    setBlob(null)
    dispatch({ type: 'RESET' })
  }, [])

  return {
    state,
    progress,
    startZipGeneration: startZipGeneration,
    downloadZip,
    reset
  }
}
